import React, { useState, useEffect, useMemo } from 'react';
import ClinicFunnelOverviewVisualization from './ClinicFunnelOverviewVisualization';
import {
  DEFAULT_AGENCY_SHARE,
  computeUnitPlan,
  computeRecepcaoPlan,
  splitByDezena,
  resolveActiveCpl,
  computeTotals,
  computeAlerts
} from '../../utils/funnelEngine';

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtInt = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtPct = (v, d = 1) => `${(Number(v || 0) * 100).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })}%`;
const fmtSigned = (v) => `${v > 0 ? '+' : ''}${fmtBRL(v)}`;

const SOURCE_LABEL = {
  clinicorp: 'Auto (Clinicorp)',
  ultimo_funil: 'Último funil',
  manual: 'Manual'
};

const LEGACY_CONVERT_WARNING = 'Este rascunho vem de um funil calculado com 100% da meta e agora usa 75% (Agência/CRC). Ao salvar, será criado um funil NOVO do mesmo mês (o antigo continua salvo) e os cards de meta de leads passam a usar o novo, com meta de leads de 75%. Salvar mesmo assim?';

const TH = 'px-3 py-3 font-bold text-xs uppercase text-slate-600 whitespace-nowrap';
const TH_GROUP = 'px-3 py-1.5 font-bold text-xs uppercase tracking-wider text-slate-700 text-center border-l border-slate-200';
const INPUT = 'px-2 py-1.5 md:py-1 text-sm md:text-xs border border-slate-500 rounded outline-none focus:border-primary placeholder:text-slate-500';

// Recalcula plano, dezenas e derivados de uma unidade conforme o % Agencia/CRC vigente.
// agencyShare do funil = null => funil legado (100%, sem divisao Agencia/Recepcao).
function recalculateUnit(unit, draft) {
  const cplTipo = unit.cplTipo || 'fixo';
  const cplFixo = Number(unit.cplFixo !== undefined && unit.cplFixo !== null ? unit.cplFixo : unit.cplMedio || 0);
  const normalized = { ...unit, cplTipo, cplFixo };
  const activeCpl = resolveActiveCpl(normalized);

  const reportShare = draft.agencyShare === undefined || draft.agencyShare === null ? null : Number(draft.agencyShare);
  const own = unit.agencyShare === undefined || unit.agencyShare === null || unit.agencyShare === '' ? null : Number(unit.agencyShare);
  const share = reportShare === null ? null : (own !== null ? own : reportShare);

  const meta = Number(unit.metaReceita) || 0;
  const ticket = Number(unit.ticketMedio) || 0;
  const plan = computeUnitPlan(meta, ticket, activeCpl, draft.rates, share);
  const newSplits = splitByDezena(plan, draft.dezenaBusinessDays, draft.rates.bigStretchMultiplier || 1.25);
  const old = unit.dezenaBreakdown || [];
  const dezenaBreakdown = newSplits.map((ns, i) => ({
    ...ns,
    realizadoLeads: old[i]?.realizadoLeads || 0,
    realizadoAgendamentos: old[i]?.realizadoAgendamentos || 0,
    realizadoComparecimentos: old[i]?.realizadoComparecimentos || 0,
    realizadoFechamentos: old[i]?.realizadoFechamentos || 0,
    realizadoInvestimento: old[i]?.realizadoInvestimento || 0
  }));

  const derived = share === null
    ? { legacyShare: true, agencyShareEffective: 1, metaCrc: meta, metaRecepcao: 0, recepcao: null }
    : {
      legacyShare: false,
      agencyShareEffective: share,
      metaCrc: plan.metaCrc,
      metaRecepcao: plan.metaRecepcao,
      recepcao: computeRecepcaoPlan(meta, share, Number(unit.ticketRecepcao) > 0 ? Number(unit.ticketRecepcao) : ticket)
    };

  return { ...normalized, cplMedio: activeCpl, ...plan, ...derived, dezenaBreakdown };
}

function recalculateAll(draft) {
  return { ...draft, units: draft.units.map(u => recalculateUnit(u, draft)) };
}

// Campo de % com texto local: so valida/aplica ao confirmar (blur ou Enter). Apagar nao recalcula nada.
// value: fracao (0-1) ou null; allowEmpty: vazio confirma como null (segue o padrao do funil).
function PercentInput({ value, onCommit, allowEmpty = false, placeholder, className, ariaLabel, id }) {
  const toText = (v) => (v === null || v === undefined ? '' : String(Math.round(Number(v) * 10000) / 100));
  const [text, setText] = useState(toText(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(toText(value)); }, [value, focused]);

  const commit = () => {
    const trimmed = text.trim().replace(',', '.');
    if (trimmed === '') {
      if (allowEmpty) onCommit(null);
      setText(toText(allowEmpty ? null : value)); // vazio invalido: volta ao valor atual
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) { setText(toText(value)); return; }
    const clamped = Math.min(n, 100);
    onCommit(clamped / 100);
    setText(String(Math.round(clamped * 100) / 100));
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      placeholder={placeholder}
      className={className}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={e => setText(e.target.value)}
      onBlur={() => { setFocused(false); commit(); }}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } else if (e.key === 'Escape') { setText(toText(value)); } }}
    />
  );
}

function SourcesPanel({ u }) {
  const s = u.sources;
  if (!s) {
    return <p className="text-xs text-slate-600">Fontes disponíveis ao gerar ou atualizar o rascunho (não são gravadas no funil salvo).</p>;
  }
  const clinicorpStatus = u.clinicorpMatched === true ? { t: 'Unidade casada', c: 'text-emerald-800' }
    : u.clinicorpMatched === false ? { t: 'NÃO casou com o Clinicorp', c: 'text-red-700 font-bold' }
      : { t: 'Sem conta Clinicorp vinculada', c: 'text-slate-600' };
  const Row = ({ label, children }) => (
    <div className="flex gap-2 text-xs"><dt className="w-28 shrink-0 font-bold text-slate-600">{label}</dt><dd className="text-slate-800 min-w-0">{children}</dd></div>
  );
  const accountList = (ids, names, failed, total, noAccess) => (ids.length === 0 && !noAccess ? <span className="text-slate-500">nenhuma</span> : (
    <div>
      {total > 0 && <p className="text-slate-600">{total} {total === 1 ? 'conta' : 'contas'}{noAccess > 0 ? ' (' + noAccess + ' sem acesso)' : ''}</p>}
      <ul className="space-y-0.5">
        {ids.map((id, i) => (
          <li key={id} className={failed.includes(id) ? 'text-red-700' : ''}>
            {names[i] || 'Conta sem nome'} <span className="font-mono text-slate-500">{id}</span>
            {failed.includes(id) && <strong> · falhou</strong>}
          </li>
        ))}
      </ul>
      {noAccess > 0 && s.failedNoAccessCount > 0 && <p className="text-red-700">{s.failedNoAccessCount} {s.failedNoAccessCount === 1 ? 'falha' : 'falhas'} em contas sem acesso</p>}
    </div>
  ));
  return (
    <dl className="space-y-1.5">
      <Row label="Contas Meta">{accountList(s.metaAccountIds || [], s.accountNames || [], s.failedMetaAccountIds || [], s.metaAccountsTotal ?? (s.metaAccountIds || []).length, s.metaAccountsNoAccess || 0)}</Row>
      <Row label="Contas Google">{accountList(s.googleAccountIds || [], s.googleAccountNames || [], s.failedGoogleAccountIds || [], s.googleAccountsTotal ?? (s.googleAccountIds || []).length, s.googleAccountsNoAccess || 0)}</Row>
      <Row label="Clinicorp">
        {s.clinicorpUnitName ? <span>{s.clinicorpUnitName} · </span> : null}<span className={clinicorpStatus.c}>{clinicorpStatus.t}</span>
      </Row>
      <Row label="Rotina clinic-data">{s.routineName || <span className="text-slate-500">{u.routineId ? 'vinculada (nome oculto)' : 'nenhuma'}</span>}</Row>
      <Row label="Origem meta">{SOURCE_LABEL[u.metaReceitaSource === 'clinicorp' ? 'clinicorp' : 'manual']}{u.metaReceitaSource !== 'clinicorp' ? ' / último funil' : ''}</Row>
      <Row label="Origem ticket">{SOURCE_LABEL[s.ticketSource] || 'Manual'}</Row>
      <Row label="Origem CPL fixo">{SOURCE_LABEL[s.cplSource] || 'Manual'}</Row>
      {(s.warnings || []).length > 0 && (
        <ul className="mt-2 space-y-1">
          {s.warnings.map((w, i) => (
            <li key={i} className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]" aria-hidden="true">warning</span>{w}
            </li>
          ))}
        </ul>
      )}
    </dl>
  );
}

function CplCompare({ u, onUse }) {
  const active = Number(u.cplMedio) || 0;
  const leads = Number(u.plannedLeads) || 0;
  const real = Number(u.realizadoLeads) > 0 ? Number(u.realizadoInvestimento || 0) / Number(u.realizadoLeads) : 0;
  const rows = [
    { key: 'anterior', label: 'Média 3M', value: Number(u.cplAnterior) || 0 },
    { key: 'real', label: 'Real (mês atual)', value: real }
  ];
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-700">CPL escolhido: <strong>{fmtBRL(active)}</strong> → investimento <strong>{fmtBRL(active * leads)}</strong></p>
      {rows.map(r => {
        const delta = active - r.value;
        const pct = r.value > 0 ? delta / r.value : null;
        const invAlt = r.value * leads;
        return (
          <div key={r.key} className="text-xs border border-slate-200 rounded-lg p-2 bg-white">
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold text-slate-700">{r.label}: {r.value > 0 ? fmtBRL(r.value) : '—'}</span>
              <button
                type="button"
                disabled={!(r.value > 0)}
                onClick={() => onUse(r.value)}
                className="px-2 py-1 rounded-md bg-primary/10 text-on-primary-fixed-variant font-bold hover:bg-primary/20 disabled:opacity-40"
              >
                Usar este valor
              </button>
            </div>
            {r.value > 0 ? (
              <p className="mt-1 text-slate-700">
                Escolhido vs {r.label}: <strong>{fmtSigned(delta)}</strong>{pct !== null ? ` (${pct > 0 ? '+' : ''}${(pct * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)` : ''}
                {' · '}com este CPL o investimento seria {fmtBRL(invAlt)} ({fmtSigned(invAlt - active * leads)})
              </p>
            ) : <p className="mt-1 text-slate-600">Sem dado para comparar.</p>}
          </div>
        );
      })}
    </div>
  );
}

export default function ClinicFunnelDraftEditor({ draft, onChange, onSave, isSaving }) {
  const [reportName, setReportName] = useState('Novo Funil Projetado');
  const [localDraft, setLocalDraft] = useState(() => (draft && draft.units ? recalculateAll(draft) : draft));
  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'preview'
  const [expanded, setExpanded] = useState({});
  const [recepOpen, setRecepOpen] = useState(false);
  // rascunho legado convertido (opt-in) para 75%: salvar cria funil NOVO
  const [convertedFromLegacy, setConvertedFromLegacy] = useState(false);

  useEffect(() => {
    // Rederiva plano/CRC/Recepcao ao receber um rascunho novo (idempotente; nao dispara onChange)
    setLocalDraft(draft && draft.units ? recalculateAll(draft) : draft);
  }, [draft]);

  const commit = (newDraft) => {
    setLocalDraft(newDraft);
    onChange(newDraft);
  };

  const isLegacy = localDraft.agencyShare === undefined || localDraft.agencyShare === null;
  const reportShare = isLegacy ? null : Number(localDraft.agencyShare);

  const totals = useMemo(() => computeTotals(localDraft.units), [localDraft.units]);
  const alertsByUnit = useMemo(() => localDraft.units.map(u => computeAlerts(u)), [localDraft.units]);
  const alertCount = alertsByUnit.reduce((s, a) => s + a.length, 0);
  const unitsWithAlerts = alertsByUnit.filter(a => a.length > 0).length;

  const setUnit = (idx, mutate) => {
    const units = [...localDraft.units];
    units[idx] = recalculateUnit(mutate(units[idx]), localDraft);
    commit({ ...localDraft, units });
  };

  const updateUnit = (idx, field, value) => {
    const numVal = Number(value) || 0;
    setUnit(idx, (u) => {
      const next = { ...u, [field]: numVal };
      if (field === 'metaReceita') next.metaReceitaSource = 'manual';
      if (field === 'cplFixo') { next.cplTipo = 'fixo'; } // digitou o CPL => passa a valer
      return next;
    });
  };

  const setUnitCplTipo = (idx, tipo) => setUnit(idx, (u) => ({ ...u, cplTipo: tipo }));
  const useCplValue = (idx, value) => setUnit(idx, (u) => ({ ...u, cplFixo: Math.round(value * 100) / 100, cplTipo: 'fixo' }));

  // fracao (0-1) ou null (volta ao padrao do funil); o valor digitado e mantido mesmo se igual ao padrao
  const setUnitShare = (idx, share) => setUnit(idx, (u) => ({ ...u, agencyShare: share }));

  const applyCplTipoToAll = (tipo) => commit(recalculateAll({ ...localDraft, units: localDraft.units.map(u => ({ ...u, cplTipo: tipo })) }));

  // % Agencia/CRC do funil: aplica a todas (limpa ajustes por unidade)
  const setReportShare = (share) => {
    commit(recalculateAll({ ...localDraft, agencyShare: share, units: localDraft.units.map(u => ({ ...u, agencyShare: null })) }));
  };
  const enableAgencySplit = () => {
    setConvertedFromLegacy(true);
    commit(recalculateAll({ ...localDraft, agencyShare: DEFAULT_AGENCY_SHARE }));
  };

  const isEmpty = !localDraft.units || localDraft.units.length === 0;

  const handleSave = () => {
    if (convertedFromLegacy && !isLegacy && !window.confirm(LEGACY_CONVERT_WARNING)) return;
    onSave({ ...localDraft, name: reportName });
  };

  const cplDefaultsHint = 'Digite o CPL fixo (alvo) ou use as sugestões: média dos últimos 3 meses e CPL real do mês atual.';
  const colCount = isLegacy ? 23 : 24;

  return (
    <div className="space-y-6">
      {/* Cabecalho: nome, abas, salvar */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div>
            <label htmlFor="funnel-report-name" className="sr-only">Nome do relatório</label>
            <input
              id="funnel-report-name"
              type="text"
              className="text-2xl font-bold font-headline outline-none border-b-2 border-transparent hover:border-slate-200 focus:border-primary transition-colors bg-transparent"
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              placeholder="Nome do Relatório"
            />
            <p className="text-sm text-slate-600 mt-1">Ref: {new Date(localDraft.referenceMonth).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200 text-xs font-semibold">
              <button type="button" aria-pressed={activeTab === 'editor'} onClick={() => setActiveTab('editor')}
                className={`px-4 py-1.5 rounded-lg transition-all ${activeTab === 'editor' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                Modo Edição
              </button>
              <button type="button" aria-pressed={activeTab === 'preview'} onClick={() => setActiveTab('preview')}
                className={`px-4 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'preview' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">visibility</span>
                Pré-visualização do Relatório
              </button>
            </div>
            <button onClick={handleSave} disabled={isSaving || isEmpty} title={isEmpty ? 'Nenhuma unidade no rascunho' : undefined}
              className="bg-on-primary-fixed-variant text-white px-6 py-2 rounded-full font-bold shadow-sm hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100">
              {isSaving ? 'Salvando...' : 'Salvar Relatório'}
            </button>
          </div>
        </div>

        {activeTab === 'editor' && (
          <>
            {/* Destaque: meta de leads da agencia */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
              <div className="col-span-2 md:col-span-1 rounded-xl bg-primary/10 p-4" aria-live="polite">
                <p className="text-xs font-bold uppercase tracking-wider text-on-primary-fixed-variant">Meta de leads (agência)</p>
                <p className="text-2xl sm:text-3xl font-black font-headline text-slate-900 break-words">{fmtInt(totals.leads)}</p>
                <p className="text-xs text-slate-700">{isLegacy ? '100% da meta (funil legado)' : `${fmtPct(reportShare, 0)} da meta de receita`}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Meta de receita</p>
                <p className="text-base sm:text-xl font-black font-headline text-slate-900 break-words">{fmtBRL(totals.metaReceita)}</p>
                {!isLegacy && <p className="text-xs text-slate-700">CRC {fmtBRL(totals.metaCrc)}</p>}
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Investimento projetado</p>
                <p className="text-base sm:text-xl font-black font-headline text-slate-900 break-words">{fmtBRL(totals.investimento)}</p>
                <p className="text-xs text-slate-700">CPL médio {fmtBRL(totals.cplMedio)} · {fmtPct(totals.pctInvestReceita)} da receita</p>
              </div>
              <div className={`rounded-xl p-4 ${alertCount > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50'}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Alertas</p>
                <p className="text-xl font-black font-headline text-slate-900">{alertCount}</p>
                <p className="text-xs text-slate-700">{alertCount > 0 ? `em ${unitsWithAlerts} ${unitsWithAlerts === 1 ? 'unidade' : 'unidades'}` : 'Nenhum alerta'}</p>
              </div>
            </div>

            {/* Premissas */}
            <div className="mt-4 rounded-xl border border-slate-200 p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">Premissas do funil</h3>
              <div className="flex flex-wrap gap-x-8 gap-y-3 items-end text-xs text-slate-800">
                {isLegacy ? (
                  <div className="max-w-md">
                    <p className="font-bold">Funil legado: calculado com 100% da meta.</p>
                    <p className="text-slate-600 mb-2">Sem divisão Agência/Recepção. Os números deste funil não são reinterpretados.</p>
                    <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">Ao converter para 75% e salvar, será criado um funil NOVO do mesmo mês e os cards de meta de leads passam a usar a meta de 75%.</p>
                    <button type="button" onClick={enableAgencySplit} className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white font-bold hover:bg-slate-50">
                      Usar divisão Agência/CRC ({fmtPct(DEFAULT_AGENCY_SHARE, 0)}) neste rascunho
                    </button>
                  </div>
                ) : (
                  <div>
                    {convertedFromLegacy && (
                      <p role="note" className="text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2 max-w-xl">
                        Rascunho convertido de um funil de 100%: ao salvar, será criado um funil NOVO do mesmo mês (o antigo continua salvo) e os cards de meta de leads passam a usar este, com meta de 75%.
                      </p>
                    )}
                    <label htmlFor="agency-share" className="block font-bold mb-1">% da meta para Agência/CRC</label>
                    <div className="flex items-center gap-2">
                      <PercentInput id="agency-share" ariaLabel="% da meta para Agência/CRC" className={`w-20 ${INPUT}`}
                        value={reportShare} onCommit={setReportShare} />
                      <span>%</span>
                      <span className="text-slate-600">Recepção: <strong>{fmtPct(1 - reportShare, 0)}</strong> (aplica a todas as unidades; ajuste por unidade na tabela)</span>
                    </div>
                  </div>
                )}
                <div>
                  <p className="font-bold mb-1">Taxas do funil</p>
                  <p>Leads → Agend. <strong>{fmtPct(localDraft.rates.leadToApptRate, 0)}</strong> · Agend. → Comp. <strong>{fmtPct(localDraft.rates.apptToAttendRate, 0)}</strong> · Comp. → Fech. <strong>{fmtPct(localDraft.rates.leadToCloseRate / ((localDraft.rates.leadToApptRate * localDraft.rates.apptToAttendRate) || 1), 0)}</strong> (= {fmtPct(localDraft.rates.leadToCloseRate, 1)} leads → fech.)</p>
                </div>
                <div>
                  <p className="font-bold mb-1">Dias úteis das dezenas</p>
                  <p>D1 <strong>{localDraft.dezenaBusinessDays[0]}</strong> · D2 <strong>{localDraft.dezenaBusinessDays[1]}</strong> · D3 <strong>{localDraft.dezenaBusinessDays[2]}</strong> (divididas por comparecimentos)</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 mt-4 border-t border-slate-100">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Aplicar a todas o CPL:</span>
                {[['fixo', 'Fixo (digitado)', 'Usar o CPL fixo digitado'], ['anterior', 'Média 3M', 'Aplicar média de CPL dos últimos 3 meses a todas as unidades'], ['real', 'Real (mês atual)', 'Aplicar o CPL real do mês atual a todas as unidades']].map(([tipo, label, title]) => (
                  <button key={tipo} type="button" onClick={() => applyCplTipoToAll(tipo)} title={title}
                    className="px-3 py-1 rounded-lg text-xs font-bold border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-sm transition-colors">
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-600">{cplDefaultsHint}</p>
            </div>
          </>
        )}
      </div>

      {activeTab === 'preview' ? (
        <div className="animate-fade-in">
          <div className="mb-4 bg-sky-50 border border-sky-200 text-sky-900 px-4 py-3 rounded-xl text-xs flex items-center justify-between">
            <span className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">info</span>
              Você está na pré-visualização completa. Todos os cálculos, KPIs e gráficos refletem as alterações atuais do rascunho antes de salvar.
            </span>
            <button onClick={() => setActiveTab('editor')} className="font-bold underline hover:text-sky-950">Voltar para edição</button>
          </div>
          <ClinicFunnelOverviewVisualization report={{ ...localDraft, name: reportName }} />
        </div>
      ) : isEmpty ? (
        <div role="status" className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-700">
          <span className="material-symbols-outlined text-4xl text-slate-400" aria-hidden="true">inbox</span>
          <p className="font-bold mt-2">Nenhuma unidade no rascunho</p>
          <p className="text-sm text-slate-600">Volte, selecione ao menos uma unidade e gere o funil novamente.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-auto max-h-[75vh]">
            <table className="w-full text-left text-sm font-body border-separate border-spacing-0">
              <thead className="sticky top-0 z-20 bg-slate-50">
                <tr>
                  <th scope="col" rowSpan={2} className={`${TH} md:sticky md:left-0 md:z-30 bg-slate-50 border-b border-slate-200`}>Unidade</th>
                  <th scope="colgroup" colSpan={isLegacy ? 2 : 3} className={TH_GROUP}>Meta</th>
                  <th scope="colgroup" colSpan={5} className={TH_GROUP}>Funil {isLegacy ? '(100%)' : 'CRC / Agência'}</th>
                  <th scope="colgroup" colSpan={3} className={TH_GROUP}>Dezenas (comparecimentos)</th>
                  <th scope="colgroup" colSpan={5} className={TH_GROUP}>CPL e investimento</th>
                  <th scope="colgroup" colSpan={6} className={TH_GROUP}>Realizado</th>
                  <th scope="col" rowSpan={2} className={`${TH} border-b border-slate-200`}>Detalhes</th>
                </tr>
                <tr className="border-b border-slate-200">
                  <th scope="col" className={`${TH} border-l border-slate-200`} title="Meta de RECEITA total da unidade (Clinicorp, editável)">Meta (R$)</th>
                  <th scope="col" className={TH}>Ticket médio</th>
                  {!isLegacy && <th scope="col" className={TH} title="Ajuste por unidade do % da meta para Agência/CRC (vazio = padrão do funil)">% Agência</th>}
                  <th scope="col" className={`${TH} border-l border-slate-200`} title={isLegacy ? 'Funil legado: 100% da meta de receita' : 'Meta de receita × % Agência/CRC'}>{isLegacy ? 'Meta no funil (R$)' : 'Meta CRC (R$)'}</th>
                  <th scope="col" className={TH}>Fech.</th>
                  <th scope="col" className={`${TH} text-on-primary-fixed-variant`} title="Leads necessários no mês para a agência">Leads necessários</th>
                  <th scope="col" className={TH}>Agend.</th>
                  <th scope="col" className={TH}>Comp.</th>
                  <th scope="col" className={`${TH} border-l border-slate-200`}>D1</th>
                  <th scope="col" className={TH}>D2</th>
                  <th scope="col" className={TH}>D3</th>
                  <th scope="col" className={`${TH} border-l border-slate-200`}>Tipo CPL</th>
                  <th scope="col" className={TH} title="CPL fixo digitado (alvo)">CPL fixo</th>
                  <th scope="col" className={TH}>CPL ativo</th>
                  <th scope="col" className={TH}>Invest. projetado</th>
                  <th scope="col" className={TH} title="Investimento projetado ÷ meta de receita">% invest./receita</th>
                  <th scope="col" className={`${TH} border-l border-slate-200`}>Invest. real (R$)</th>
                  <th scope="col" className={TH}>Leads</th>
                  <th scope="col" className={TH}>Agend.</th>
                  <th scope="col" className={TH}>Comp.</th>
                  <th scope="col" className={TH}>Fech.</th>
                  <th scope="col" className={TH}>Receita</th>
                </tr>
              </thead>
              <tbody>
                {localDraft.units.map((u, i) => {
                  const tipo = u.cplTipo || 'fixo';
                  const alerts = alertsByUnit[i];
                  const hasError = alerts.some(a => a.severity === 'error');
                  const isOpen = !!expanded[i];
                  const key = u.unitMappingId || u.routineId || i;
                  const real = Number(u.realizadoLeads) > 0 ? Number(u.realizadoInvestimento || 0) / Number(u.realizadoLeads) : 0;
                  const dez = u.dezenaBreakdown || [];
                  const cell = 'px-3 py-3 border-b border-slate-100 whitespace-nowrap';
                  return (
                    <React.Fragment key={key}>
                      <tr className="group hover:bg-slate-50">
                        <td className={`${cell} md:sticky md:left-0 md:z-10 bg-white group-hover:bg-slate-50 font-medium text-slate-900 min-w-[8rem] md:min-w-[11rem]`}>
                          <div className="flex items-center gap-1.5">
                            {alerts.length > 0 && (
                              <button type="button" aria-expanded={isOpen} onClick={() => setExpanded(e => ({ ...e, [i]: true }))}
                                title={alerts.map(a => a.message).join('\n')}
                                aria-label={`${alerts.length} alerta(s): ${alerts.map(a => a.message).join(' ')} Abrir detalhes.`}
                                className={`shrink-0 rounded focus:outline focus:outline-2 ${hasError ? 'text-red-700' : 'text-amber-700'}`}>
                                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">warning</span>
                              </button>
                            )}
                            <span className="truncate max-w-[7rem] md:max-w-[10rem]" title={u.routineName}>{u.routineName}</span>
                          </div>
                        </td>
                        <td className={`${cell} border-l border-slate-100`}>
                          <input type="number" step="0.01" aria-label={`Meta de receita de ${u.routineName}`} className={`w-28 ${INPUT}`} value={u.metaReceita} onChange={e => updateUnit(i, 'metaReceita', e.target.value)} />
                          {u.metaReceitaSource === 'clinicorp' && <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Auto</span>}
                        </td>
                        <td className={cell}><input type="number" step="0.01" aria-label={`Ticket médio de ${u.routineName}`} className={`w-24 ${INPUT}`} value={u.ticketMedio} onChange={e => updateUnit(i, 'ticketMedio', e.target.value)} /></td>
                        {!isLegacy && (
                          <td className={cell}>
                            <div className="flex items-center gap-1">
                              <PercentInput allowEmpty ariaLabel={`% Agência de ${u.routineName}`} placeholder={String(Math.round(reportShare * 100))}
                                className={`w-16 ${INPUT} ${u.agencyShare !== null && u.agencyShare !== undefined ? 'ring-2 ring-primary/20 border-primary font-bold' : ''}`}
                                value={u.agencyShare} onCommit={(v) => setUnitShare(i, v)} />
                              <span className="text-xs text-slate-600">%</span>
                            </div>
                          </td>
                        )}
                        <td className={`${cell} border-l border-slate-100 text-slate-800`}>{fmtBRL(u.metaCrc)}</td>
                        <td className={cell}>{fmtInt(u.plannedFechamentos)}</td>
                        <td className={`${cell} font-black text-on-primary-fixed-variant`}>{fmtInt(u.plannedLeads)}</td>
                        <td className={cell}>{fmtInt(u.plannedAgendamentos)}</td>
                        <td className={cell}>{fmtInt(u.plannedComparecimentos)}</td>
                        <td className={`${cell} border-l border-slate-100`}>{fmtInt(dez[0]?.plannedComparecimentos)}</td>
                        <td className={cell}>{fmtInt(dez[1]?.plannedComparecimentos)}</td>
                        <td className={cell}>{fmtInt(dez[2]?.plannedComparecimentos)}</td>
                        <td className={`${cell} border-l border-slate-100`}>
                          <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-xs font-bold" role="group" aria-label={`Tipo de CPL de ${u.routineName}`}>
                            {[['fixo', 'Fixo', 'Usar o CPL fixo digitado'], ['anterior', 'Ant. (3M)', 'Usar a média de CPL dos últimos 3 meses'], ['real', 'Real', 'Usar o CPL real atual']].map(([t, label, title]) => (
                              <button key={t} type="button" aria-pressed={tipo === t} onClick={() => setUnitCplTipo(i, t)} title={title}
                                className={`px-2 py-1 rounded transition-colors ${tipo === t ? 'bg-on-primary-fixed-variant text-white shadow-sm' : 'text-slate-700 hover:text-slate-900'}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td className={cell}>
                          <input type="number" step="0.01" aria-label={`CPL fixo de ${u.routineName}`}
                            className={`w-24 ${INPUT} ${tipo === 'fixo' ? 'ring-2 ring-primary/20 border-primary font-bold' : ''}`}
                            value={u.cplFixo !== undefined ? u.cplFixo : u.cplMedio} onChange={e => updateUnit(i, 'cplFixo', e.target.value)} />
                          <div className="mt-1 flex gap-1">
                            <button type="button" aria-label={`Usar média 3M ${fmtBRL(u.cplAnterior || 0)} como CPL fixo de ${u.routineName}`} disabled={!(Number(u.cplAnterior) > 0)} onClick={() => useCplValue(i, Number(u.cplAnterior))}
                              className="px-1.5 py-0.5 rounded border border-sky-300 bg-sky-50 text-sky-900 text-xs font-semibold hover:bg-sky-100 disabled:opacity-40" title="Usar a média dos últimos 3 meses como CPL fixo">
                              3M {fmtBRL(u.cplAnterior || 0)}
                            </button>
                            <button type="button" aria-label={`Usar CPL real ${fmtBRL(real)} como CPL fixo de ${u.routineName}`} disabled={!(real > 0)} onClick={() => useCplValue(i, real)}
                              className="px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-900 text-xs font-semibold hover:bg-emerald-100 disabled:opacity-40" title="Usar o CPL real do mês atual como CPL fixo">
                              Real {fmtBRL(real)}
                            </button>
                          </div>
                        </td>
                        <td className={`${cell} font-black text-slate-900`}>{fmtBRL(u.cplMedio || 0)}</td>
                        <td className={`${cell} font-bold text-slate-800`}>{fmtBRL(u.plannedInvestimento || 0)}</td>
                        <td className={`${cell} ${u.metaReceita > 0 && u.plannedInvestimento / u.metaReceita > 0.3 ? 'text-amber-800 font-bold' : ''}`}>
                          {u.metaReceita > 0 ? fmtPct(u.plannedInvestimento / u.metaReceita) : '—'}
                        </td>
                        <td className={`${cell} border-l border-slate-100`}><input type="number" step="0.01" aria-label={`Investimento real de ${u.routineName}`} className={`w-24 ${INPUT}`} value={u.realizadoInvestimento} onChange={e => updateUnit(i, 'realizadoInvestimento', e.target.value)} /></td>
                        <td className={cell}><input type="number" aria-label={`Leads realizados de ${u.routineName}`} className={`w-16 ${INPUT}`} value={u.realizadoLeads} onChange={e => updateUnit(i, 'realizadoLeads', e.target.value)} /></td>
                        <td className={cell}><input type="number" aria-label={`Agendamentos realizados de ${u.routineName}`} className={`w-16 ${INPUT}`} value={u.realizadoAgendamentos} onChange={e => updateUnit(i, 'realizadoAgendamentos', e.target.value)} /></td>
                        <td className={cell}><input type="number" aria-label={`Comparecimentos realizados de ${u.routineName}`} className={`w-16 ${INPUT}`} value={u.realizadoComparecimentos} onChange={e => updateUnit(i, 'realizadoComparecimentos', e.target.value)} /></td>
                        <td className={cell}><input type="number" aria-label={`Fechamentos realizados de ${u.routineName}`} className={`w-16 ${INPUT}`} value={u.realizadoFechamentos} onChange={e => updateUnit(i, 'realizadoFechamentos', e.target.value)} /></td>
                        <td className={cell}><input type="number" step="0.01" aria-label={`Receita realizada de ${u.routineName}`} className={`w-24 ${INPUT}`} value={u.realizadoReceita} onChange={e => updateUnit(i, 'realizadoReceita', e.target.value)} /></td>
                        <td className={`${cell} border-l border-slate-100`}>
                          <button type="button" aria-expanded={isOpen} aria-label={`${isOpen ? 'Recolher' : 'Expandir'} detalhes de ${u.routineName}`}
                            onClick={() => setExpanded(e => ({ ...e, [i]: !e[i] }))}
                            className="px-2 py-1 rounded-md border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1">
                            <span className="material-symbols-outlined text-[16px]" aria-hidden="true">{isOpen ? 'expand_less' : 'expand_more'}</span>Fontes
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50">
                          <td colSpan={colCount} className="px-4 py-4 border-b border-slate-200">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-full">
                              <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Fontes da unidade</h4>
                                <SourcesPanel u={u} />
                              </div>
                              <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">CPL: escolhido vs sugestões</h4>
                                <CplCompare u={u} onUse={(v) => useCplValue(i, v)} />
                              </div>
                              <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Alertas</h4>
                                {alerts.length === 0 ? <p className="text-xs text-emerald-800">Nenhum alerta para esta unidade.</p> : (
                                  <ul className="space-y-1">
                                    {alerts.map(a => (
                                      <li key={a.code} className={`text-xs rounded px-2 py-1 border ${a.severity === 'error' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>{a.message}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-20">
                <tr className="bg-slate-100 font-bold text-slate-900">
                  <td className="px-3 py-3 md:sticky md:left-0 md:z-30 bg-slate-100 border-t-2 border-slate-300 whitespace-nowrap">TOTAL ({localDraft.units.length})</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300 whitespace-nowrap">{fmtBRL(totals.metaReceita)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">—</td>
                  {!isLegacy && <td className="px-3 py-3 border-t-2 border-slate-300">—</td>}
                  <td className="px-3 py-3 border-t-2 border-slate-300 whitespace-nowrap">{fmtBRL(totals.metaCrc)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtInt(totals.fechamentos)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300 text-on-primary-fixed-variant font-black">{fmtInt(totals.leads)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtInt(totals.agendamentos)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtInt(totals.comparecimentos)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtInt(totals.dezenas[0])}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtInt(totals.dezenas[1])}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtInt(totals.dezenas[2])}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">—</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">—</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300 whitespace-nowrap" title="CPL médio ponderado = investimento ÷ leads">{fmtBRL(totals.cplMedio)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300 whitespace-nowrap">{fmtBRL(totals.investimento)}</td>
                  <td className="px-3 py-3 border-t-2 border-slate-300">{fmtPct(totals.pctInvestReceita)}</td>
                  <td colSpan={7} className="px-3 py-3 border-t-2 border-slate-300 text-xs font-normal text-slate-600">Realizado: preencha por unidade</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Recepcao (resto da meta), separada e colapsavel */}
          {!isLegacy && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
              <button type="button" aria-expanded={recepOpen} onClick={() => setRecepOpen(o => !o)}
                className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left">
                <span className="flex items-center gap-2 font-headline font-bold text-slate-800">
                  <span className="material-symbols-outlined text-[20px] text-primary" aria-hidden="true">support_agent</span>
                  Recepção ({fmtPct(1 - reportShare, 0)} da meta) — fora da meta de leads da agência
                </span>
                <span className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-slate-700">
                  <span>Meta {fmtBRL(totals.metaRecepcao)}</span>
                  <span>Indicações {fmtInt(totals.recepLeads)}</span>
                  <span className="material-symbols-outlined text-[20px]" aria-hidden="true">{recepOpen ? 'expand_less' : 'expand_more'}</span>
                </span>
              </button>
              {recepOpen && (
                <div className="overflow-x-auto px-6 pb-6">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th scope="col" className={TH}>Unidade</th>
                        <th scope="col" className={TH}>Meta Recepção (R$)</th>
                        <th scope="col" className={TH} title="Ticket médio da trilha Recepção (padrão = ticket da unidade)">Ticket Recepção</th>
                        <th scope="col" className={TH}>Fech. (30%)</th>
                        <th scope="col" className={TH}>Comp.</th>
                        <th scope="col" className={TH}>Agend.</th>
                        <th scope="col" className={TH}>Indicações / Leads</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {localDraft.units.map((u, i) => (
                        <tr key={u.unitMappingId || u.routineId || i}>
                          <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{u.routineName}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtBRL(u.metaRecepcao)}</td>
                          <td className="px-3 py-2">
                            <input type="number" step="0.01" aria-label={`Ticket Recepção de ${u.routineName}`} className={`w-24 ${INPUT}`}
                              placeholder={String(u.ticketMedio || 0)}
                              value={u.ticketRecepcao !== null && u.ticketRecepcao !== undefined ? u.ticketRecepcao : ''}
                              onChange={e => setUnit(i, (x) => ({ ...x, ticketRecepcao: e.target.value === '' ? null : Number(e.target.value) }))} />
                          </td>
                          <td className="px-3 py-2">{fmtInt(u.recepcao?.fechamentos)}</td>
                          <td className="px-3 py-2">{fmtInt(u.recepcao?.comparecimentos)}</td>
                          <td className="px-3 py-2">{fmtInt(u.recepcao?.agendamentos)}</td>
                          <td className="px-3 py-2 font-bold">{fmtInt(u.recepcao?.leads)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 font-bold text-slate-900">
                        <td className="px-3 py-2">TOTAL</td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtBRL(totals.metaRecepcao)}</td>
                        <td className="px-3 py-2">—</td>
                        <td className="px-3 py-2">{fmtInt(totals.recepFechamentos)}</td>
                        <td className="px-3 py-2">{fmtInt(totals.recepComparecimentos)}</td>
                        <td className="px-3 py-2">{fmtInt(totals.recepAgendamentos)}</td>
                        <td className="px-3 py-2">{fmtInt(totals.recepLeads)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
